import { useCallback, useEffect, useState, useRef } from "react";
import "./styles.scss";
import { PiPaperPlaneRightFill } from "react-icons/pi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppSelector, useAppDispatch } from "@/redux/hooks";
import {
  prevMessagesFetch,
  setNewMessage,
  updateMessageStatus,
} from "@/redux/slice/chat/prevMessages";
import { useAuth } from "@/contexts/auth-context";
import { formatChatTime, isTimeOver24HoursAgo } from "@/utils/function";
import { socket } from "@/socket";
import { v4 as uuidv4 } from "uuid";

interface IChat {
  teacherId: string;
  content: string;
  senderUserType: string;
  receiver: string;
  sender: string;
  id: string;
  status?: "pending" | "sent" | "failed";
  profilePicture: string;
  firstName: string;
  lastName: string;
  lastMessage: {
    createdAt: string;
    content: string;
    id: string;
  };
}

interface IChats {
  selectedMessage: IChat;
  setSelectedMessage: (a: any) => void;
}

const Chat = ({ selectedMessage, setSelectedMessage }: IChats) => {
  const { token, details } = useAuth();
  const dispatch = useAppDispatch();
  const [msg, setMsg] = useState<string>("");
  const endRef = useRef<HTMLDivElement>(null);

  const chatFetch = useCallback(() => {
    dispatch(
      prevMessagesFetch({ token: token!, id: selectedMessage.teacherId })
    );
  }, [token, selectedMessage.teacherId, dispatch]);

  useEffect(() => {
    chatFetch();
  }, [chatFetch]);

  const { data, loading, error } = useAppSelector((state) => state.prevChats);
  const onSend = () => {
    if (!socket) return;
    if (!msg || !selectedMessage.teacherId) {
      alert(!msg ? "Type a message" : "Something went wrong...");
    } else {
      if (socket && socket.connected) {
        const newMsg = {
          transaction: uuidv4(),
          content: msg,
          senderUserType: "user",
          receiverId: selectedMessage.teacherId,
          status: "pending",
          createdAt: new Date(),
        };
        dispatch(
          setNewMessage({
            ...newMsg,
          })
        );

        socket.emit("sendMessage", newMsg);
        socket.on("messageSent", (response) => {
          if (response) {
            dispatch(
              updateMessageStatus({ id: newMsg.transaction, status: "sent" })
            );
          } else {
            dispatch(
              updateMessageStatus({ id: newMsg.transaction, status: "failed" })
            );
          }
        });
        socket.on("errorOccurred", (error: any) => {
          console.warn("Connection failed:", error);
          dispatch(
            updateMessageStatus({ id: newMsg.transaction, status: "failed" })
          );
        });
        setMsg("");
      } else {
        alert("Something went wrong...");
      }
    }
  };

  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (res: any) => {
      dispatch(setNewMessage(res));
    };

    socket.on("newMessage", handleNewMessage);

    return () => {
      socket?.off("newMessage", handleNewMessage);
    };
  }, [dispatch]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [data]);

  return (
    <div className="notification-details">
      <div className="chat-container">
        <div className="details-head">
          <h2 className="details-title">{`${selectedMessage.firstName} ${selectedMessage.lastName}`}</h2>
        </div>

        {loading ? (
          <p>Loading</p>
        ) : data.length < 1 ? (
          <p className="chat-start">Start a conversation</p>
        ) : (
          <div
            className="chat-body"
            style={{
              // height: "670px",
              overflowY: "auto",
              // backgroundColor: "red",
            }}
          >
            {data.map((chat, index) => {
              return (
                <div className="chat-item-container" key={index}>
                  <div className="chat-date">
                    {!chat?.createdAt ? null : formatChatTime(chat?.createdAt)}
                  </div>
                  <div
                    className={`chat-item ${
                      chat.sender === "me" ? "sent" : "received"
                    }`}
                  >
                    <div className="image-container">
                      <img
                        src={chat.profilePicture}
                        alt={chat.sender}
                        className="chat-avatar"
                      />
                    </div>
                    <div className="chat-content">
                      <p className="chat-sender">
                        {chat.senderUserType === "user"
                          ? "You"
                          : `${selectedMessage.firstName} ${selectedMessage.lastName}`}
                      </p>
                      <p className="chat-message">{chat.content}</p>
                      {chat.senderUserType !== "user" ? null : chat.status ? (
                        chat.status === "pending" ? (
                          <FontAwesomeIcon icon={["fas", "clock"]} />
                        ) : chat.status === "sent" ? (
                          <FontAwesomeIcon icon={["fas", "check"]} />
                        ) : (
                          <FontAwesomeIcon icon={["fas", "circle-notch"]} />
                        )
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSend();
          }}
          className="chat-footer"
        >
          <input
            onChange={(e) => setMsg(e.target.value?.trimStart())}
            className="chat-input"
            placeholder="Your Message"
            value={msg}
          />
          <button type="submit" className="chat-send">
            <PiPaperPlaneRightFill size={30} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default Chat;
